import React, { useEffect, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Users, Lock, Unlock, Plus, X, ChevronDown, ChevronUp, Check } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import { nomeModalita, haFisso } from '../../utils/compenso';

const MESI_FULL = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
                   'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

const euro = (n) => `€${(n || 0).toFixed(2)}`;
const testoNum = (v) => (v === null || v === undefined ? '' : String(v));
const numONull = (v) => { const t = String(v ?? '').trim().replace(',', '.'); if (t === '') return null; const n = parseFloat(t); return isNaN(n) ? NaN : n; };

export default function PagheMensili() {
  const { user } = useAuth();
  const isRoot = user?.ruolo === 'ROOT';
  const puoModificare = isRoot || !!user?.permessi?.presenze?.edit;
  const puoCedolino = puoFare(user, 'dati_paghe', 'edit');

  const oggi = new Date();
  const [anno, setAnno] = useState(oggi.getFullYear());
  const [mese, setMese] = useState(oggi.getMonth() + 1);
  const [righe, setRighe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [espansoId, setEspansoId] = useState(null);
  const [draft, setDraft] = useState(null); // { lordo, addebiti:[{descrizione,importo}], bonifico, nuovaDescr, nuovoImporto }
  const [salvando, setSalvando] = useState(false);

  const navMese = (dir) => {
    let m = mese + dir, a = anno;
    if (m > 12) { m = 1; a++; }
    if (m < 1)  { m = 12; a--; }
    setMese(m); setAnno(a); setEspansoId(null);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/paghe/${anno}/${mese}`);
      setRighe(r.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [anno, mese]);

  useEffect(() => { load(); }, [load]);

  const espandi = (r) => {
    if (espansoId === r.dipendente.id) { setEspansoId(null); setDraft(null); return; }
    setEspansoId(r.dipendente.id);
    setDraft({
      // fisso non ancora inserito nel mese: proposta la mensilità della scheda (12/10/2026)
      // FISSO_ORE (03/11/2026): draft.lordo è la parte fissa, le ore restano lordo_base dal server
      lordo: String(r.dipendente.modalita_compenso === 'FISSO_ORE'
        ? (r.inserita ? (r.importo_fisso ?? 0) : (r.dipendente.compenso_fisso ?? 0))
        : r.inserita ? (r.lordo_base ?? r.importo_lordo ?? 0) : (r.dipendente.modalita_compenso === 'FISSO' ? (r.dipendente.compenso_fisso ?? 0) : 0)),
      addebiti: (r.addebiti || []).map(a => ({ descrizione: a.descrizione, importo: a.importo })),
      aggiuntivi: (r.aggiuntivi || []).map(a => ({ descrizione: a.descrizione, importo: a.importo })),
      nuovoAggDescr: '', nuovoAggImporto: '',
      // fisso non ancora inserito: netto proposto sulla mensilità della scheda, non sullo 0 del server
      bonifico: String(r.bonifico_importo ?? (!r.inserita && r.dipendente.modalita_compenso === 'FISSO'
        ? Math.round(((r.dipendente.compenso_fisso ?? 0) - (r.totale_addebiti || 0) - (r.trattenute || 0)) * 100) / 100
        : !r.inserita && r.dipendente.modalita_compenso === 'FISSO_ORE'
        ? Math.round(((r.importo_netto ?? 0) + (r.dipendente.compenso_fisso ?? 0)) * 100) / 100
        : (r.importo_netto ?? 0))),
      nuovaDescr: '', nuovoImporto: '',
      ced_collab: testoNum(r.contributi?.cedolino?.contributi_collab),
      ced_societa: testoNum(r.contributi?.cedolino?.contributi_societa),
      ced_irpef: testoNum(r.contributi?.cedolino?.irpef),
    });
  };

  // Trattenute: quelle scritte dal cedolino se compilate, altrimenti l'ultima stima del server
  // (calcolata sul lordo salvato: se si cambia il lordo si aggiorna dopo SALVA BOZZA).
  const trattenuteDraft = (r) => {
    if (!draft) return 0;
    const collab = numONull(draft.ced_collab);
    if (collab === null || isNaN(collab)) return r.trattenute || 0;
    const irpef = numONull(draft.ced_irpef);
    return collab + (irpef && !isNaN(irpef) ? irpef : 0);
  };

  const totAggDraft = () => draft ? draft.aggiuntivi.reduce((s, a) => s + a.importo, 0) : 0;
  const numLordo = () => parseFloat(draft.lordo.replace(',', '.')) || 0;
  // lordo del mese prima degli aggiuntivi: mensilità (FISSO), ore (ORARIO), fisso + ore (FISSO_ORE)
  const lordoBaseDraft = (r) => {
    const m = r.dipendente.modalita_compenso;
    if (m === 'FISSO') return numLordo();
    return (r.lordo_base ?? r.importo_lordo) + (m === 'FISSO_ORE' ? numLordo() : 0);
  };
  const nettoDraft = (r) => {
    if (!draft) return 0;
    const lordo = lordoBaseDraft(r) + totAggDraft();
    const totAddebiti = draft.addebiti.reduce((s, a) => s + a.importo, 0);
    return lordo - totAddebiti - trattenuteDraft(r);
  };

  const payload = (r) => {
    const body = {
      importo_lordo: haFisso(r.dipendente.modalita_compenso) ? parseFloat(draft.lordo.replace(',', '.')) : undefined,
      addebiti: draft.addebiti,
      aggiuntivi: draft.aggiuntivi,
    };
    if (puoCedolino && r.contributi) {
      const ced = { contributi_collab: numONull(draft.ced_collab), contributi_societa: numONull(draft.ced_societa), irpef: numONull(draft.ced_irpef) };
      if (Object.values(ced).some(v => Number.isNaN(v) || (v !== null && v < 0))) throw new Error('Valori del cedolino non validi');
      body.cedolino = ced;
    }
    return body;
  };

  const aggiungiAddebito = () => {
    const importo = parseFloat(String(draft.nuovoImporto).replace(',', '.'));
    if (!draft.nuovaDescr.trim() || isNaN(importo)) { alert('Descrizione e importo obbligatori'); return; }
    setDraft(d => ({ ...d, addebiti: [...d.addebiti, { descrizione: d.nuovaDescr.trim(), importo }], nuovaDescr: '', nuovoImporto: '' }));
  };

  const aggiungiAggiuntivo = () => {
    const importo = parseFloat(String(draft.nuovoAggImporto).replace(',', '.'));
    if (!draft.nuovoAggDescr.trim() || isNaN(importo) || importo <= 0) { alert('Descrizione e importo (maggiore di zero) obbligatori'); return; }
    setDraft(d => ({ ...d, aggiuntivi: [...d.aggiuntivi, { descrizione: d.nuovoAggDescr.trim(), importo }], nuovoAggDescr: '', nuovoAggImporto: '' }));
  };
  // ricopia i compensi aggiuntivi del mese precedente (es. un fisso mensile non ancora a contratto)
  const copiaAggiuntiviMesePrima = async (r) => {
    const m = mese === 1 ? 12 : mese - 1, a = mese === 1 ? anno - 1 : anno;
    try {
      const res = await api.get(`/paghe/${a}/${m}`);
      const prima = (res.data.find(x => x.dipendente.id === r.dipendente.id)?.aggiuntivi || []).map(x => ({ descrizione: x.descrizione, importo: x.importo }));
      if (!prima.length) { alert(`Nessun compenso aggiuntivo in ${MESI_FULL[m-1]} ${a}`); return; }
      setDraft(d => ({ ...d, aggiuntivi: [...d.aggiuntivi, ...prima] }));
    } catch (e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const rimuoviAddebito = (i) => {
    setDraft(d => ({ ...d, addebiti: d.addebiti.filter((_, idx) => idx !== i) }));
  };

  const salvaBozza = async (r) => {
    const lordo = haFisso(r.dipendente.modalita_compenso) ? parseFloat(draft.lordo.replace(',', '.')) : undefined;
    if (haFisso(r.dipendente.modalita_compenso) && (isNaN(lordo) || lordo < 0)) { alert('Importo mensile non valido'); return; }
    setSalvando(true);
    try {
      await api.put(`/paghe/${r.dipendente.id}/${anno}/${mese}`, payload(r));
      await load();
      setEspansoId(null); setDraft(null);
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  };

  const chiudiMese = async (r) => {
    const bonifico = parseFloat(draft.bonifico.replace(',', '.'));
    if (isNaN(bonifico) || bonifico < 0) { alert('Importo bonifico non valido'); return; }
    if (!window.confirm(`Chiudere ${MESI_FULL[mese-1]} ${anno} per ${r.dipendente.cognome} ${r.dipendente.nome} con bonifico €${bonifico.toFixed(2)}?\n\nDopo la chiusura sarà modificabile solo da ROOT.`)) return;
    setSalvando(true);
    try {
      // salva prima la bozza corrente (lordo/addebiti/cedolino), poi chiude col bonifico indicato
      await api.put(`/paghe/${r.dipendente.id}/${anno}/${mese}`, payload(r));
      await api.post(`/paghe/${r.dipendente.id}/${anno}/${mese}/chiudi`, { bonifico_importo: bonifico });
      await load();
      setEspansoId(null); setDraft(null);
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  };

  // Riapri (solo ROOT, 12/10/2026): torna BOZZA, lordo a ore e contributi stimati di nuovo calcolati dal vivo
  const riapriMese = async (r) => {
    if (!window.confirm(`Riaprire ${MESI_FULL[mese-1]} ${anno} per ${r.dipendente.cognome} ${r.dipendente.nome}?\n\nTorna in BOZZA: il compenso a ore e i contributi stimati verranno ricalcolati, il bonifico registrato (€${(r.bonifico_importo ?? 0).toFixed(2)}) andrà reinserito alla nuova chiusura. L'operazione resta nel changelog.`)) return;
    setSalvando(true);
    try {
      await api.post(`/paghe/${r.dipendente.id}/${anno}/${mese}/riapri`);
      await load();
      setEspansoId(null); setDraft(null);
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  };

  const totLordo    = righe.reduce((s, r) => s + r.importo_lordo, 0);
  const totAddebiti = righe.reduce((s, r) => s + r.totale_addebiti, 0);
  const totNetto     = righe.reduce((s, r) => s + r.importo_netto, 0);
  const totTrattenute = righe.reduce((s, r) => s + (r.trattenute || 0), 0);
  const vedeCosto = righe.some(r => r.contributi);
  const totCosto = righe.reduce((s, r) => s + (r.contributi?.costo_societa || 0), 0);
  const totContribSocieta = righe.reduce((s, r) => s + (r.contributi?.contributi_societa || 0), 0);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'24px' }}>
        <div>
          <h1 style={s.title}>PAGHE MENSILI</h1>
          <p style={s.sub}>Lordo, addebiti, contributi e bonifico per dipendente, per mese</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          <button style={s.navBtn} onClick={() => navMese(-1)}><ChevronLeft size={16}/></button>
          <span style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight:'800', minWidth:'160px', textAlign:'center' }}>
            {MESI_FULL[mese-1].toUpperCase()} {anno}
          </span>
          <button style={s.navBtn} onClick={() => navMese(1)}><ChevronRight size={16}/></button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:`repeat(${vedeCosto ? 5 : 4},1fr)`, gap:'12px', marginBottom:'24px' }}>
        <div style={s.kpi}>
          <div style={s.kpiLabel}>LORDO TOTALE</div>
          <div style={s.kpiVal}>€{totLordo.toFixed(2)}</div>
        </div>
        <div style={s.kpi}>
          <div style={s.kpiLabel}>ADDEBITI TOTALI</div>
          <div style={{ ...s.kpiVal, color:'#EF4444' }}>€{totAddebiti.toFixed(2)}</div>
        </div>
        <div style={s.kpi}>
          <div style={s.kpiLabel}>TRATTENUTE</div>
          <div style={{ ...s.kpiVal, color:'#EF4444' }}>€{totTrattenute.toFixed(2)}</div>
        </div>
        <div style={s.kpi}>
          <div style={s.kpiLabel}>NETTO TOTALE</div>
          <div style={{ ...s.kpiVal, color:'#22C55E' }}>€{totNetto.toFixed(2)}</div>
        </div>
        {vedeCosto && (
          <div style={s.kpi} title="Lordo + contributi INPS a carico della società">
            <div style={s.kpiLabel}>COSTO SOCIETÀ</div>
            <div style={s.kpiVal}>€{totCosto.toFixed(2)}</div>
            <div style={{ fontSize:'var(--font-size-xs)', color:'#555' }}>di cui contributi €{totContribSocieta.toFixed(2)}</div>
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:'60px', color:'#555' }}>Caricamento...</div>
      ) : righe.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px', color:'#555' }}>
          <Users size={40} color="var(--text-dim)" style={{ marginBottom:'12px' }}/>
          <p>Nessun dipendente attivo in {MESI_FULL[mese-1].toLowerCase()} {anno}.</p>
        </div>
      ) : (
        <div style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'12px', overflow:'hidden' }}>
          <div className="testata-via" style={s.rowHead}>
            <div style={{ flex:1 }}>DIPENDENTE</div>
            <div style={{ width:'110px', textAlign:'right' }}>LORDO</div>
            <div style={{ width:'110px', textAlign:'right' }}>ADDEBITI</div>
            <div style={{ width:'110px', textAlign:'right' }}>TRATTENUTE</div>
            <div style={{ width:'110px', textAlign:'right' }}>NETTO</div>
            {vedeCosto && <div style={{ width:'120px', textAlign:'right' }}>COSTO SOCIETÀ</div>}
            <div style={{ width:'120px', textAlign:'right' }}>STATO</div>
            <div style={{ width:'30px' }}/>
          </div>
          {righe.map(r => {
            const aperto = espansoId === r.dipendente.id;
            return (
              <div key={r.dipendente.id}>
                <div className="riga-scheda" style={s.row} onClick={() => espandi(r)}
                  onMouseEnter={e=>e.currentTarget.style.background='var(--bg-input)'}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <div className="riga-info" style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:'700', fontSize: 'var(--font-size-sm)' }}>{r.dipendente.cognome} {r.dipendente.nome}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>
                      {nomeModalita(r.dipendente.modalita_compenso)}
                      {r.dipendente.modalita_compenso === 'FISSO_ORE' && ` (fisso €${(r.importo_fisso || 0).toFixed(2)} + ore €${(r.lordo_base || 0).toFixed(2)})`}
                      {haFisso(r.dipendente.modalita_compenso) && !r.inserita && ' — fisso non ancora inserito: apri e salva la mensilità'}
                    </div>
                  </div>
                  <div data-label="Lordo" style={{ width:'110px', textAlign:'right', fontSize: 'var(--font-size-sm)' }}>€{r.importo_lordo.toFixed(2)}</div>
                  <div data-label="Addebiti" style={{ width:'110px', textAlign:'right', fontSize: 'var(--font-size-sm)', color: r.totale_addebiti ? '#EF4444' : undefined }}>
                    {r.totale_addebiti ? `-€${r.totale_addebiti.toFixed(2)}` : '—'}
                  </div>
                  <div data-label="Trattenute" style={{ width:'110px', textAlign:'right', fontSize: 'var(--font-size-sm)', color: r.trattenute ? '#EF4444' : undefined }}>
                    {r.trattenute ? `-€${r.trattenute.toFixed(2)}` : '—'}
                    {r.irpef_mancante && <div style={{ fontSize:'var(--font-size-xs)', color:'#F59E0B' }}>+ IRPEF</div>}
                  </div>
                  <div data-label="Netto" style={{ width:'110px', textAlign:'right', fontWeight:'700', fontSize: 'var(--font-size-sm)' }}>€{r.importo_netto.toFixed(2)}</div>
                  {vedeCosto && (
                    <div data-label="Costo società" style={{ width:'120px', textAlign:'right', fontSize: 'var(--font-size-sm)' }}>
                      {r.contributi ? euro(r.contributi.costo_societa) : '—'}
                      {r.contributi && r.contributi.contributi_societa > 0 && (
                        <div style={{ fontSize:'var(--font-size-xs)', color: r.contributi.fonte === 'CEDOLINO' ? '#22C55E' : '#888' }}>
                          {r.contributi.fonte === 'CEDOLINO' ? 'da cedolino' : 'stima'}
                        </div>
                      )}
                    </div>
                  )}
                  <div data-label="Stato" style={{ width:'120px', textAlign:'right' }}>
                    {r.chiuso ? (
                      <span style={s.badgeChiuso}><Lock size={11}/> CHIUSO</span>
                    ) : (
                      <span style={s.badgeBozza}><Unlock size={11}/> BOZZA</span>
                    )}
                  </div>
                  <div style={{ width:'30px', display:'flex', justifyContent:'flex-end' }}>
                    {aperto ? <ChevronUp size={16} color="#555"/> : <ChevronDown size={16} color="#555"/>}
                  </div>
                </div>

                {aperto && draft && (
                  <div style={s.pannello} onClick={e => e.stopPropagation()}>
                    {r.chiuso && !isRoot ? (
                      <div style={{ fontSize: 'var(--font-size-sm)', color:'#888' }}>
                        Mese chiuso il {r.chiuso_il ? new Date(r.chiuso_il).toLocaleDateString('it-IT') : ''} — bonifico registrato €{(r.bonifico_importo ?? 0).toFixed(2)}.
                        Solo ROOT può modificarlo.
                        {r.contributi && (
                          <div style={{ marginTop:'8px' }}>
                            Contributi ({r.contributi.fonte === 'CEDOLINO' ? 'da cedolino' : 'stima congelata alla chiusura'}):
                            trattenuti {euro(r.contributi.contributi_collab)}, a carico società {euro(r.contributi.contributi_societa)}
                            {r.contributi.irpef ? `, IRPEF ${euro(r.contributi.irpef)}` : ''}.
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {r.chiuso && isRoot && (
                          <div style={{ fontSize: 'var(--font-size-xs)', color:'#F59E0B', marginBottom:'12px' }}>
                            Mese già chiuso — stai modificando come ROOT, verrà tracciato nel changelog.
                          </div>
                        )}
                        <div style={{ display:'flex', gap:'24px', flexWrap:'wrap' }}>
                          {r.dipendente.modalita_compenso === 'FISSO' ? (
                            <div>
                              <div style={s.campoLabel}>IMPORTO MENSILE LORDO</div>
                              <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                                <span style={{ color:'#555' }}>€</span>
                                <input type="number" step="0.01" min="0" value={draft.lordo}
                                  onChange={e => setDraft(d => ({ ...d, lordo: e.target.value }))}
                                  style={s.input}/>
                              </div>
                            </div>
                          ) : (
                            <>
                              {r.dipendente.modalita_compenso === 'FISSO_ORE' && (
                                <div>
                                  <div style={s.campoLabel}>FISSO DEL MESE (scheda €{(r.dipendente.compenso_fisso ?? 0).toFixed(2)})</div>
                                  <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                                    <span style={{ color:'#555' }}>€</span>
                                    <input type="number" step="0.01" min="0" value={draft.lordo}
                                      onChange={e => setDraft(d => ({ ...d, lordo: e.target.value }))}
                                      style={s.input}/>
                                  </div>
                                </div>
                              )}
                              <div>
                                <div style={s.campoLabel}>{r.dipendente.modalita_compenso === 'FISSO_ORE' ? 'ORE (da presenze)' : 'LORDO (da presenze)'}</div>
                                <div style={{ fontSize: 'var(--font-size-sm)', fontWeight:'700', padding:'8px 0' }}>€{(r.lordo_base ?? r.importo_lordo).toFixed(2)}</div>
                              </div>
                              {r.dipendente.modalita_compenso === 'FISSO_ORE' && (
                                <div>
                                  <div style={s.campoLabel}>LORDO FISSO + ORE</div>
                                  <div style={{ fontSize: 'var(--font-size-sm)', fontWeight:'700', padding:'8px 0' }}>€{lordoBaseDraft(r).toFixed(2)}</div>
                                </div>
                              )}
                            </>
                          )}

                          <div>
                            <div style={s.campoLabel}>IMPORTO BONIFICO (netto suggerito €{nettoDraft(r).toFixed(2)})</div>
                            <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                              <span style={{ color:'#555' }}>€</span>
                              <input type="number" step="0.01" min="0" value={draft.bonifico}
                                onChange={e => setDraft(d => ({ ...d, bonifico: e.target.value }))}
                                style={s.input}/>
                            </div>
                          </div>
                        </div>

                        {r.contributi && (
                          <div style={{ marginTop:'16px', padding:'12px', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px' }}>
                            <div style={s.campoLabel}>CONTRIBUTI INPS E IRPEF</div>
                            <div style={{ fontSize:'var(--font-size-xs)', color:'#888', marginBottom:'10px', lineHeight:1.5 }}>
                              Compensi dell'anno prima di questo mese (anche da altre società): <strong>{euro(r.contributi.cumulato_prima)}</strong>.
                              {' '}Soggetti a contributi in questo mese: <strong>{euro(r.contributi.base_contributiva)}</strong> (la parte oltre i 5.000 € annui).
                              <br/>Stima: trattenuti al collaboratore <strong>{euro(r.contributi.stima.contributi_collab)}</strong>,
                              {' '}a carico della società <strong>{euro(r.contributi.stima.contributi_societa)}</strong>.
                              {' '}Se il cedolino riporta cifre diverse, scrivile qui: valgono quelle.
                            </div>
                            {r.irpef_mancante && (
                              <div style={{ fontSize:'var(--font-size-xs)', color:'#F59E0B', marginBottom:'10px' }}>
                                Compensi annui oltre 15.000 €: c'è anche l'IRPEF, prendila dal cedolino.
                              </div>
                            )}
                            <fieldset disabled={!puoCedolino} style={{ border:'none', padding:0, margin:0, display:'flex', gap:'24px', flexWrap:'wrap' }}>
                              {[
                                ['ced_collab', 'INPS TRATTENUTA (CEDOLINO)', r.contributi.stima.contributi_collab],
                                ['ced_irpef', 'IRPEF (CEDOLINO)', 0],
                                ['ced_societa', 'INPS A CARICO SOCIETÀ', r.contributi.stima.contributi_societa],
                              ].map(([k, label, stima]) => (
                                <div key={k}>
                                  <div style={s.campoLabel}>{label}</div>
                                  <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                                    <span style={{ color:'#555' }}>€</span>
                                    <input type="number" step="0.01" min="0" value={draft[k]} placeholder={stima.toFixed(2)}
                                      onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))}
                                      style={s.input}/>
                                  </div>
                                </div>
                              ))}
                            </fieldset>
                            <div style={{ fontSize:'var(--font-size-xs)', color:'#555', marginTop:'8px' }}>
                              Campi vuoti = stima (numero in grigio). La quota a carico della società non è sul cedolino: la trovi nell'F24, altrimenti resta la stima.
                              {!puoCedolino && ' Per modificarli serve il permesso "Dati paghe".'}
                            </div>
                          </div>
                        )}

                        <div style={{ marginTop:'16px' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                            <div style={s.campoLabel}>COMPENSI AGGIUNTIVI (fisso non a contratto, premi, extra)</div>
                            <button style={{ ...s.btnSecondario, padding:'3px 8px', fontSize:'var(--font-size-xs)' }} onClick={() => copiaAggiuntiviMesePrima(r)}>Come il mese scorso</button>
                          </div>
                          <div style={{ fontSize:'var(--font-size-xs)', color:'#888', marginBottom:'4px' }}>Si sommano al lordo: contano per contributi, soglie e autocertificazione. Le ore in più vanno invece come presenze nel calendario.</div>
                          {draft.aggiuntivi.map((a, i) => (
                            <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'4px 0' }}>
                              <span style={{ flex:1, fontSize: 'var(--font-size-sm)' }}>{a.descrizione}</span>
                              <span style={{ fontSize: 'var(--font-size-sm)', color:'#22C55E' }}>+€{a.importo.toFixed(2)}</span>
                              <button style={s.iconBtn} onClick={() => setDraft(d => ({ ...d, aggiuntivi: d.aggiuntivi.filter((_, idx) => idx !== i) }))}><X size={13} color="#EF4444"/></button>
                            </div>
                          ))}
                          {draft.aggiuntivi.length > 0 && <div style={{ fontSize:'var(--font-size-xs)', color:'#888' }}>Lordo del mese con gli aggiuntivi: <strong>€{(lordoBaseDraft(r) + totAggDraft()).toFixed(2)}</strong></div>}
                          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'8px' }}>
                            <input placeholder="Descrizione (es. fisso coordinamento ottobre)" value={draft.nuovoAggDescr}
                              onChange={e => setDraft(d => ({ ...d, nuovoAggDescr: e.target.value }))}
                              style={{ ...s.input, flex:1, textAlign:'left' }}/>
                            <span style={{ color:'#555' }}>€</span>
                            <input type="number" step="0.01" min="0" placeholder="0.00" value={draft.nuovoAggImporto}
                              onChange={e => setDraft(d => ({ ...d, nuovoAggImporto: e.target.value }))}
                              style={{ ...s.input, width:'90px' }}/>
                            <button style={s.navBtn} onClick={aggiungiAggiuntivo}><Plus size={14}/></button>
                          </div>
                        </div>

                        <div style={{ marginTop:'16px' }}>
                          <div style={s.campoLabel}>ADDEBITI (abbigliamento, vitto, ecc.)</div>
                          {draft.addebiti.map((a, i) => (
                            <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'4px 0' }}>
                              <span style={{ flex:1, fontSize: 'var(--font-size-sm)' }}>{a.descrizione}</span>
                              <span style={{ fontSize: 'var(--font-size-sm)', color:'#EF4444' }}>-€{a.importo.toFixed(2)}</span>
                              <button style={s.iconBtn} onClick={() => rimuoviAddebito(i)}><X size={13} color="#EF4444"/></button>
                            </div>
                          ))}
                          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'8px' }}>
                            <input placeholder="Descrizione" value={draft.nuovaDescr}
                              onChange={e => setDraft(d => ({ ...d, nuovaDescr: e.target.value }))}
                              style={{ ...s.input, flex:1, textAlign:'left' }}/>
                            <span style={{ color:'#555' }}>€</span>
                            <input type="number" step="0.01" placeholder="0.00" value={draft.nuovoImporto}
                              onChange={e => setDraft(d => ({ ...d, nuovoImporto: e.target.value }))}
                              style={{ ...s.input, width:'90px' }}/>
                            <button style={s.navBtn} onClick={aggiungiAddebito}><Plus size={14}/></button>
                          </div>
                        </div>

                        {puoModificare && (
                          <div style={{ display:'flex', gap:'10px', marginTop:'20px' }}>
                            <button style={s.btnSecondario} disabled={salvando} onClick={() => salvaBozza(r)}>
                              <Check size={13}/> SALVA BOZZA
                            </button>
                            {!r.chiuso && (
                              <button style={s.btnPrimario} disabled={salvando} onClick={() => chiudiMese(r)}>
                                <Lock size={13}/> CHIUDI MESE
                              </button>
                            )}
                            {r.chiuso && isRoot && (
                              <button style={s.btnSecondario} disabled={salvando} onClick={() => riapriMese(r)}>
                                <Unlock size={13}/> RIAPRI MESE
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  title:    { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800', letterSpacing:'2px' },
  sub:      { color:'var(--text-muted,#888)', fontSize: 'var(--font-size-sm)', marginTop:'4px' },
  navBtn:   { background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'8px 10px', color:'var(--text,#fff)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' },
  kpi:      { background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'12px', padding:'16px 20px' },
  kpiLabel: { fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'6px' },
  kpiVal:   { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800' },
  rowHead:  { display:'flex', alignItems:'center', padding:'10px 20px', borderBottom:'1px solid var(--border,#1E1E1E)', fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'1px', color:'#555' },
  row:      { display:'flex', alignItems:'center', padding:'14px 20px', borderBottom:'1px solid var(--border,#1E1E1E)', cursor:'pointer', transition:'background 0.15s' },
  pannello: { padding:'16px 20px', borderBottom:'1px solid var(--border,#1E1E1E)', background:'var(--bg-input)' },
  campoLabel: { fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'1px', color:'#555', marginBottom:'6px' },
  input:    { width:'110px', background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'6px', padding:'6px 8px', color:'var(--text,#fff)', fontSize: 'var(--font-size-sm)', textAlign:'right' },
  iconBtn:  { background:'transparent', border:'none', cursor:'pointer', padding:'2px', display:'flex', alignItems:'center' },
  badgeChiuso: { display:'inline-flex', alignItems:'center', gap:'4px', fontSize: 'var(--font-size-xs)', fontWeight:'800', padding:'2px 8px', borderRadius:'4px', color:'#22C55E', background:'#22C55E15' },
  badgeBozza:  { display:'inline-flex', alignItems:'center', gap:'4px', fontSize: 'var(--font-size-xs)', fontWeight:'800', padding:'2px 8px', borderRadius:'4px', color:'#F59E0B', background:'#F59E0B15' },
  btnSecondario: { display:'flex', alignItems:'center', gap:'6px', background:'transparent', border:'1px solid var(--border2,#2A2A2A)', color:'var(--text,#fff)', borderRadius:'6px', padding:'8px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  btnPrimario:   { display:'flex', alignItems:'center', gap:'6px', background:'#F4600C', border:'none', color:'#fff', borderRadius:'6px', padding:'8px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
};
